import { useCallback, useEffect, useRef, useState } from 'react';
import { AppUser, CopilotMessage, TripSpec, TripCopilotPhase, Coordinates, CampingSpot, TripWeather } from '../types';
import { planTripWithCopilot } from '../services/firebaseService';
import { planTrip, PlanTripResult, PlanTripSuccess, PlanTripFailure } from '../services/tripPlannerService';
import { assessRouteWeather, rankBetterWeather } from '../services/weatherService';
import { searchPlace } from '../services/geocodingService';
import i18n from '../i18n';
import type { TripWeather as TW } from '../types';

/** Lokalisert vær-overskrift fra strukturert data (samme nøkler som CopilotPanel). */
function weatherHeadline(w: TW): string {
  const key = w.verdict === 'bad' ? 'copilot.weather.bad' : w.verdict === 'good' ? 'copilot.weather.good' : 'copilot.weather.mixed';
  return i18n.t(key, { rainy: w.rainyDays, days: w.assessedDays, temp: w.avgHighC });
}
import {
  SavedCopilotTrip, subscribeToTrips, saveTrip as saveTripToCloud,
  deleteTrip as deleteTripFromCloud, shareTrip as shareTripToCloud,
} from '../services/copilotTripService';
import {
  CopilotMemory, DEFAULT_COPILOT_MEMORY, subscribeToCopilotMemory,
  addCopilotFacts, setCopilotLearningEnabled, clearCopilotMemory,
  applyMemorySnapshotAfterClear,
} from '../services/copilotMemoryService';

/**
 * Reise-copilot tilstandsmaskin.
 *
 *   INTERVJU ('interview')  chat + chips, INGEN ruting
 *      │  tripSpec.status → 'ready'
 *      ▼
 *   FORSLAG ('proposal')    AI viser tekst-forslag, fortsatt ingen kart-rute
 *      │  confirmPlan()
 *      ▼
 *   PLANLAGT ('planned')    ruten tegnes via onDrawRoute; justeringer re-planlegger (debounced)
 *
 * localStorage-persist per bruker overlever refresh/bakgrunnslegging (D3). Debounce på
 * re-plan begrenser OSRM-last (D2).
 */

const STORAGE_PREFIX = 'fricamp_copilot_v1_';
const REPLAN_DEBOUNCE_MS = 800;

interface PersistShape {
  messages: CopilotMessage[];
  tripSpec: TripSpec;
  phase: TripCopilotPhase;
}

function storageKey(uid: string | undefined): string {
  return `${STORAGE_PREFIX}${uid || 'anon'}`;
}

// Sporing (DEV-only): feilsøking av rediger/fjern-kjeden («dødt ×»). Stille i prod-bygg.
const copilotDebug = (...args: unknown[]) => {
  if ((import.meta as any).env?.DEV) console.info('[COPILOT-EDIT]', ...args);
};

function loadPersisted(uid: string | undefined): PersistShape | null {
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages) || !parsed.tripSpec) return null;
    return parsed as PersistShape;
  } catch { return null; }
}

function profileFromUser(user: AppUser | null | undefined): Record<string, unknown> | undefined {
  if (!user) return undefined;
  const p: Record<string, unknown> = {};
  if (user.activeVehicleCategory) p.vehicleCategory = user.activeVehicleCategory;
  if (user.activeVehicleLength) p.vehicleLength = user.activeVehicleLength;
  if (Array.isArray(user.activeVehicleTerrainPrefs)) p.terrainPrefs = user.activeVehicleTerrainPrefs;
  if (user.homeCounty) p.homeCounty = user.homeCounty;
  if (user.homeMunicipality) p.homeMunicipality = user.homeMunicipality;
  if (typeof user.tier === 'number') p.tier = user.tier;
  return Object.keys(p).length ? p : undefined;
}

export interface UseTripCopilotArgs {
  user: AppUser | null | undefined;
  open: boolean;
  onDrawRoute: (result: PlanTripSuccess) => void;
  onClearRoute: () => void;
  currentPosition?: Coordinates | null;
  /** Rik AI-kontekst (favoritter/besøkt/lag/Mitt) — sendes som profile til callable. */
  context?: Record<string, unknown> | null;
  /** Favoritt-plasser m/ koordinater — brukes til å favorisere stopp langs ruten. */
  favoriteSpots?: CampingSpot[];
}

export interface UseTripCopilotState {
  phase: TripCopilotPhase;
  messages: CopilotMessage[];
  tripSpec: TripSpec;
  suggestedChoices: string[];
  thinking: boolean;      // AI-runde pågår
  planning: boolean;      // ruteberegning pågår
  statusMsg: string;      // "Tegner ruten…" o.l.
  error: string | null;
  planResult: PlanTripSuccess | null;
  savedTrips: SavedCopilotTrip[];
  saving: boolean;
  /** «Copiloten lærer om deg»: aktivert? (default true) */
  learningEnabled: boolean;
  /** Lærte, varige preferanser om brukeren (vises i tannhjulet). */
  learnedFacts: string[];
  /** Værvurdering for reisemålet (når en tur er planlagt). null før planlegging. */
  weather: TripWeather | null;
  /** Beste alternativ med bedre vær (etter «finn bedre vær»). null hvis ingen/ikke søkt. */
  weatherAlt: TripWeather | null;
  /** Be copiloten foreslå + verifisere reisemål med bedre værmelding. */
  suggestBetterWeather: () => void;
  /** Rediger start/mål fra rutefeltet («Endre») → full re-planlegging m/ nye stopp. */
  updateEndpoint: (which: 'origin' | 'destination', coords: Coordinates, name: string) => void;
  /** Fjern et anbefalt stopp → re-planlegg uten det (andre plasser foreslås i stedet). */
  removeStop: (index: number) => void;
  sendMessage: (text: string) => void;
  confirmPlan: () => void;
  reset: () => void;
  /** Kjør siste tur på nytt (etter en feil) — re-sender historikken uten ny bruker-boble. */
  retry: () => void;
  saveCurrentTrip: () => Promise<void>;
  loadTrip: (trip: SavedCopilotTrip) => void;
  deleteSavedTrip: (tripId: string) => Promise<void>;
  shareCurrentTrip: () => Promise<string | null>;
  openSharedSpec: (tripSpec: TripSpec, title?: string) => void;
  /** Skru læring av/på. */
  setLearningEnabled: (enabled: boolean) => void;
  /** Tøm alt copiloten har lært. */
  clearLearnedMemory: () => void;
}

const INITIAL_SPEC: TripSpec = { status: 'asking' };

export function useTripCopilot({ user, open, onDrawRoute, onClearRoute, currentPosition, context, favoriteSpots }: UseTripCopilotArgs): UseTripCopilotState {
  const uid = user?.uid;
  const [phase, setPhase] = useState<TripCopilotPhase>('interview');
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [tripSpec, setTripSpec] = useState<TripSpec>(INITIAL_SPEC);
  const [suggestedChoices, setSuggestedChoices] = useState<string[]>([]);
  const [thinking, setThinking] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [planResult, setPlanResult] = useState<PlanTripSuccess | null>(null);
  const [savedTrips, setSavedTrips] = useState<SavedCopilotTrip[]>([]);
  const [currentTripId, setCurrentTripId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [memory, setMemory] = useState<CopilotMemory>(DEFAULT_COPILOT_MEMORY);
  const [weather, setWeather] = useState<TripWeather | null>(null);
  const [weatherAlt, setWeatherAlt] = useState<TripWeather | null>(null);

  const restoredRef = useRef(false);              // har restore kjørt minst én gang
  const restoredForUid = useRef<string | undefined | null>(null); // hvilken uid sist restaurert
  const greetedRef = useRef(false);
  const replanTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Nyeste verdier for callbacks uten stale closures.
  const specRef = useRef(tripSpec); specRef.current = tripSpec;
  const phaseRef = useRef(phase); phaseRef.current = phase;
  const messagesRef = useRef(messages); messagesRef.current = messages;
  const positionRef = useRef(currentPosition); positionRef.current = currentPosition;
  const contextRef = useRef(context); contextRef.current = context;
  const favsRef = useRef(favoriteSpots); favsRef.current = favoriteSpots;
  const memoryRef = useRef(memory); memoryRef.current = memory;
  const weatherRef = useRef(weather); weatherRef.current = weather;
  const weatherAltRef = useRef(weatherAlt); weatherAltRef.current = weatherAlt;
  const planResultRef = useRef(planResult); planResultRef.current = planResult;
  // Stopp brukeren aktivt har fjernet — ekskluderes ved re-planlegging (per tur).
  const excludedStopIds = useRef<Set<string>>(new Set());
  // Tak på antall stopp etter manuell fjerning: re-plan skal IKKE refylle plassen med en
  // nabo-plass (samme kommune-etikett → ser identisk ut → «dødt ×»). null = intet tak.
  const stopCapRef = useRef<number | null>(null);
  // Etter «tøm minne»: ignorer stale onSnapshot med gamle facts (ISSUE-004).
  const memoryClearedAtRef = useRef(0);

  // Gjenopprett fra localStorage — én gang PER uid. Auth laster asynkront, så uid går
  // ofte undefined→ekte etter mount; vi må restaurere på nytt når den ekte uid ankommer
  // (ellers gjenopprettes aldri den innloggede brukerens lagrede utkast).
  useEffect(() => {
    if (restoredForUid.current === uid) return;
    const persisted = loadPersisted(uid);
    if (persisted) {
      setMessages(persisted.messages);
      setTripSpec(persisted.tripSpec);
      setPhase(persisted.phase);
      greetedRef.current = persisted.messages.length > 0;
    }
    restoredForUid.current = uid;
    restoredRef.current = true;
  }, [uid]);

  // Persister (throttlet av React-batching; billig JSON).
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem(storageKey(uid), JSON.stringify({ messages, tripSpec, phase }));
    } catch { /* WKWebView/kvote — bevisst stille */ }
  }, [uid, messages, tripSpec, phase]);

  const terrainPrefs = user?.activeVehicleTerrainPrefs;

  // Kjør ruteplanlegging (ved bekreftelse og ved justering).
  const runPlan = useCallback(async (spec: TripSpec) => {
    copilotDebug('runPlan start', { origin: spec.origin, destination: spec.destination, ekskludert: [...excludedStopIds.current] });
    setPlanning(true);
    setError(null);
    setStatusMsg(i18n.t('copilot.status.planning'));
    let result: PlanTripResult;
    try {
      // Ignorer tjenestens (norske) statustekster — vis én lokalisert «Planlegger…».
      result = await planTrip(spec, { terrainPrefs, onStatus: () => setStatusMsg(i18n.t('copilot.status.planning')), currentPosition: positionRef.current, favoriteSpots: favsRef.current, excludeSpotIds: [...excludedStopIds.current], maxStops: stopCapRef.current ?? undefined });
    } catch (e) {
      copilotDebug('runPlan KASTET', e);
      setPlanning(false);
      setStatusMsg('');
      setError(i18n.t('copilot.error.plan_failed'));
      return;
    }
    setPlanning(false);
    setStatusMsg('');
    if (!result.ok) {
      // tsconfig er ikke-strict → union narrower ikke på boolean-literal; eksplisitt cast.
      copilotDebug('runPlan FEILET', (result as PlanTripFailure).error, (result as PlanTripFailure).message);
      setError((result as PlanTripFailure).message);
      return;
    }
    const success = result as PlanTripSuccess;
    copilotDebug('runPlan OK → tegner', { stopp: success.stops.map((s) => `${s.id}:${s.name || ''}`), fra: success.resolvedOriginName, til: success.resolvedDestName });
    setPlanResult(success);
    onDrawRoute(success);

    // Værvurdering for reisemålet (best-effort, blokkerer ikke rutetegning). Nullstill
    // gammelt alternativ; nytt mål → ny vurdering.
    setWeatherAlt(null);
    void assessRouteWeather(success.toCoords, {
      days: spec.days,
      startDate: spec.startDate,
      place: success.resolvedDestName || spec.destination || 'reisemålet',
    }).then(setWeather).catch(() => setWeather(null));
  }, [terrainPrefs, onDrawRoute]);

  const scheduleReplan = useCallback((spec: TripSpec) => {
    stopCapRef.current = null; // chat-justering → tillat nytt stopp-utvalg
    if (replanTimer.current) clearTimeout(replanTimer.current);
    replanTimer.current = setTimeout(() => { void runPlan(spec); }, REPLAN_DEBOUNCE_MS);
  }, [runPlan]);

  // Geokode + værsjekk AI-ens foreslåtte alternativer, ranger, og tilby å bytte til det
  // beste HVIS det faktisk har bedre vær enn nåværende mål. Best-effort (stille ved feil).
  const processWeatherAlternatives = useCallback(async (names: string[]) => {
    const current = weatherRef.current;
    if (!current || !Array.isArray(names) || names.length === 0) return;
    const spec = specRef.current;
    const assessed: TripWeather[] = [];
    for (const name of names.slice(0, 3)) {
      try {
        const hits = await searchPlace(name, ['no', 'se', 'dk']);
        const hit = hits && hits[0];
        if (!hit) continue;
        const coords: Coordinates = { latitude: parseFloat(hit.lat), longitude: parseFloat(hit.lon) };
        if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) continue;
        assessed.push(await assessRouteWeather(coords, { days: spec.days, startDate: spec.startDate, place: name }));
      } catch { /* hopp over ett alternativ */ }
    }
    const best = rankBetterWeather(current, assessed);
    if (!best) return;
    setWeatherAlt(best);
    const destName = (current.place || '').split(',')[0];
    const chips = [i18n.t('copilot.alt.switch_to', { place: best.place }), i18n.t('copilot.alt.keep', { place: destName })];
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: i18n.t('copilot.alt.better', { place: best.place, headline: weatherHeadline(best) }),
      chips, ts: Date.now(),
    }]);
    setSuggestedChoices(chips);
  }, []);

  // Kjernen: én samtale-tur. `visibleUserText` legges til som bruker-boble (tom = kickoff).
  const sendTurn = useCallback(async (visibleUserText: string) => {
    const base = visibleUserText
      ? [...messagesRef.current, { role: 'user' as const, content: visibleUserText, ts: Date.now() }]
      : messagesRef.current;
    if (visibleUserText) setMessages(base);
    setThinking(true);
    setError(null);
    setSuggestedChoices([]);

    try {
      // Send lærte preferanser med profilen (kun når læring er på + ikke nylig tømt).
      // memoryClearedAtRef > 0 = lokal clear venter på server-bekreftelse — ALDRI send
      // facts da, ellers hilser AI fortsatt med «dere to med barna» (ISSUE-004).
      const mem = memoryRef.current;
      const baseProfile = contextRef.current ?? profileFromUser(user);
      const profile: Record<string, unknown> = { ...(baseProfile || {}) };
      if (mem.enabled && mem.facts.length && memoryClearedAtRef.current === 0) {
        profile.learnedPreferences = mem.facts;
      }
      // Dagens dato (for at AI-en kan tolke «til helgen» → startDate) + værmelding for
      // reisemålet så AI-en kan informere/anbefale (den beregner aldri vær selv).
      profile.today = new Date().toISOString().slice(0, 10);
      profile.language = i18n.language || i18n.resolvedLanguage || 'no';
      const w = weatherRef.current;
      if (w && w.verdict !== 'unknown') {
        const alt = weatherAltRef.current;
        profile.tripWeather = {
          place: w.place, verdict: w.verdict, headline: w.headline,
          ...(alt ? { alternatives: [{ place: alt.place, verdict: alt.verdict, headline: alt.headline }] } : {}),
        };
      }
      const res = await planTripWithCopilot({ messages: base, profile });
      const assistant: CopilotMessage = {
        role: 'assistant', content: res.reply, chips: res.suggestedChoices, ts: Date.now(),
      };
      setMessages((prev) => [...(visibleUserText ? prev : base), assistant]);
      setSuggestedChoices(res.suggestedChoices || []);
      setTripSpec(res.tripSpec);

      // «Lær om deg»: lagre nye varige preferanser AI-en fanget opp (kun når på + innlogget).
      const learnings = Array.isArray((res as any).learnings) ? (res as any).learnings as string[] : [];
      if (mem.enabled && uid && learnings.length) {
        void addCopilotFacts(uid, mem.facts, learnings, Date.now()).catch(() => { /* stille */ });
      }

      // AI foreslo alternative reisemål (bedre vær) → verifiser dem mot faktisk prognose.
      const wAlts = Array.isArray((res as any).weatherAlternatives) ? (res as any).weatherAlternatives as string[] : [];
      if (wAlts.length) void processWeatherAlternatives(wAlts);

      // Fase-overgang: klar til forslag når nok info finnes (men ikke ruting ennå).
      if (res.tripSpec.status === 'ready' && phaseRef.current === 'interview') {
        setPhase('proposal');
      }
      // Justering i planlagt fase → debounced re-plan.
      if (res.tripSpec.status === 'ready' && phaseRef.current === 'planned') {
        scheduleReplan(res.tripSpec);
      }
    } catch (e: any) {
      const msg = /permission-denied|premium|testfunksjon/i.test(String(e?.message || e))
        ? i18n.t('copilot.error.access')
        : i18n.t('copilot.error.no_reply');
      setError(msg);
    } finally {
      setThinking(false);
    }
  }, [user, scheduleReplan, processWeatherAlternatives]);

  const suggestBetterWeather = useCallback(() => {
    if (thinking) return;
    void sendTurn('Finn et lignende reisemål med bedre værmelding.');
  }, [thinking, sendTurn]);

  // «Endre» start/mål i rutefeltet på en copilot-rute: oppdater spec-en med brukerens
  // EKSAKTE valg (navn+koordinat pinnes — planTrip re-geokoder ikke) og re-planlegg hele
  // turen, så anbefalte stopp/severdigheter/vær følger den nye ruten (ikke stale state).
  const updateEndpoint = useCallback((which: 'origin' | 'destination', coords: Coordinates, name: string) => {
    copilotDebug('updateEndpoint', which, name, coords);
    stopCapRef.current = null; // ny korridor → nytt stopp-utvalg er ønsket
    const spec: TripSpec = { ...specRef.current };
    if (which === 'origin') {
      spec.origin = name; spec.resolvedOriginName = name; spec.resolvedOriginCoords = coords;
    } else {
      spec.destination = name; spec.resolvedDestName = name; spec.resolvedDestCoords = coords;
    }
    setTripSpec(spec);
    // Optimistisk: oppdater tegningen med nytt endepunkt med én gang (synlig respons);
    // re-planleggingen under bytter så ut stoppene til den nye korridoren.
    const pr = planResultRef.current;
    if (pr) {
      const optimistic: PlanTripSuccess = which === 'origin'
        ? { ...pr, fromCoords: coords, resolvedOriginName: name }
        : { ...pr, toCoords: coords, resolvedDestName: name };
      setPlanResult(optimistic);
      onDrawRoute(optimistic);
    }
    if (spec.status === 'ready') void runPlan(spec);
  }, [runPlan, onDrawRoute]);

  // Fjern et anbefalt stopp: OPTIMISTISK først — stoppet forsvinner umiddelbart fra
  // rute/liste (ellers oppleves trykket som dødt mens re-planleggingen jobber i det
  // stille). Deretter ekskluderes plassen og turen re-planlegges, så andre kandidater
  // langs ruten kan foreslås i stedet.
  const removeStop = useCallback((index: number) => {
    const pr = planResultRef.current;
    copilotDebug('removeStop', { index, harPlanResult: !!pr, antallStopp: pr?.stops.length, stoppId: pr?.stops[index]?.id, specStatus: specRef.current.status });
    if (pr && pr.stops[index]) {
      const stop = pr.stops[index];
      if (stop.id) excludedStopIds.current.add(stop.id);
      const optimistic: PlanTripSuccess = { ...pr, stops: pr.stops.filter((_, i) => i !== index) };
      stopCapRef.current = optimistic.stops.length; // re-plan skal ikke refylle plassen
      setPlanResult(optimistic);
      copilotDebug('removeStop → optimistisk tegning', { nyeStopp: optimistic.stops.map((s) => s.id), ekskludert: [...excludedStopIds.current], stoppTak: stopCapRef.current });
      onDrawRoute(optimistic); // tegn uten stoppet med én gang
    }
    if (specRef.current.status === 'ready') void runPlan(specRef.current);
    else copilotDebug('removeStop: spec ikke ready — ingen replan');
  }, [runPlan, onDrawRoute]);

  // Hils når panelet åpnes første gang uten historikk.
  useEffect(() => {
    if (open && restoredRef.current && !greetedRef.current && messages.length === 0 && !thinking) {
      greetedRef.current = true;
      void sendTurn('');
    }
  }, [open, messages.length, thinking, sendTurn]);

  const sendMessage = useCallback((text: string) => {
    const t = (text || '').trim();
    if (!t || thinking) return;
    void sendTurn(t);
  }, [thinking, sendTurn]);

  // Feil-retry: re-send historikken som den er (tom kickoff-tekst = ingen ny bruker-boble).
  // Dekker både mislykket hilsen (tom historikk → hils på nytt) og mislykket svar-tur
  // (den feilede bruker-boblen ligger allerede i historikken → den re-sendes).
  const retry = useCallback(() => {
    if (thinking) return;
    setError(null);
    void sendTurn('');
  }, [thinking, sendTurn]);

  const confirmPlan = useCallback(() => {
    if (specRef.current.status !== 'ready') return;
    setPhase('planned');
    void runPlan(specRef.current);
  }, [runPlan]);

  const reset = useCallback(() => {
    if (replanTimer.current) clearTimeout(replanTimer.current);
    excludedStopIds.current = new Set();
    stopCapRef.current = null;
    greetedRef.current = false;
    setMessages([]);
    setTripSpec(INITIAL_SPEC);
    setPhase('interview');
    setSuggestedChoices([]);
    setThinking(false);
    setPlanning(false);
    setStatusMsg('');
    setError(null);
    setPlanResult(null);
    setWeather(null);
    setWeatherAlt(null);
    setCurrentTripId(undefined);
    onClearRoute();
    try { localStorage.removeItem(storageKey(uid)); } catch { /* stille */ }
  }, [uid, onClearRoute]);

  // ─── Sky-lagring (Fase 3) ───

  // Abonner på brukerens lagrede turer.
  useEffect(() => {
    if (!uid) { setSavedTrips([]); return; }
    return subscribeToTrips(uid, setSavedTrips);
  }, [uid]);

  // Abonner på «lær om deg»-minnet (default på når doc mangler).
  useEffect(() => {
    if (!uid) { setMemory(DEFAULT_COPILOT_MEMORY); memoryClearedAtRef.current = 0; return; }
    return subscribeToCopilotMemory(uid, (incoming) => {
      const { memory: next, clearedAt } = applyMemorySnapshotAfterClear(incoming, memoryClearedAtRef.current);
      memoryClearedAtRef.current = clearedAt;
      setMemory(next);
    });
  }, [uid]);

  const setLearningEnabled = useCallback((enabled: boolean) => {
    if (!uid) return;
    setMemory((m) => ({ ...m, enabled })); // optimistisk
    void setCopilotLearningEnabled(uid, enabled, Date.now()).catch(() => { /* stille */ });
  }, [uid]);

  const clearLearnedMemory = useCallback(() => {
    if (!uid) return;
    const now = Date.now();
    memoryClearedAtRef.current = now;
    setMemory((m) => ({ ...m, facts: [], updatedAt: now })); // optimistisk
    // Behold lokal clear selv om sky-skriv feiler (App Check / offline) — memoryClearedAtRef
    // sørger for at sendTurn ikke re-injiserer gamle facts fra et stale snapshot.
    void clearCopilotMemory(uid, now).catch(() => { /* stille — lokal clear gjelder */ });
  }, [uid]);

  const saveCurrentTrip = useCallback(async () => {
    if (!uid || specRef.current.status !== 'ready') return;
    setSaving(true);
    try {
      const id = await saveTripToCloud(
        uid,
        { id: currentTripId, tripSpec: specRef.current, messages: messagesRef.current },
        Date.now(),
      );
      setCurrentTripId(id);
    } catch {
      setError(i18n.t('copilot.error.save_failed'));
    } finally {
      setSaving(false);
    }
  }, [uid, currentTripId]);

  // Felles: restaurer en spec (fra lagret tur eller delelenke) og planlegg hvis klar.
  const restoreSpec = useCallback((spec: TripSpec, msgs: CopilotMessage[], tripId?: string) => {
    if (replanTimer.current) clearTimeout(replanTimer.current);
    excludedStopIds.current = new Set(); // ny tur-kontekst → nullstill fjernede stopp
    stopCapRef.current = null;
    greetedRef.current = true;
    setCurrentTripId(tripId);
    setMessages(msgs);
    setTripSpec(spec);
    setSuggestedChoices([]);
    setError(null);
    if (spec.status === 'ready') {
      setPhase('planned');
      void runPlan(spec);
    } else {
      setPhase('interview');
    }
  }, [runPlan]);

  const loadTrip = useCallback((trip: SavedCopilotTrip) => {
    restoreSpec(trip.tripSpec, trip.messages || [], trip.id);
  }, [restoreSpec]);

  const openSharedSpec = useCallback((spec: TripSpec, title?: string) => {
    const intro: CopilotMessage = {
      role: 'assistant',
      content: title ? i18n.t('copilot.shared.intro_titled', { title }) : i18n.t('copilot.shared.intro'),
      ts: 0,
    };
    restoreSpec(spec, [intro]); // ny (ikke-eid) tur → ingen currentTripId
  }, [restoreSpec]);

  const deleteSavedTrip = useCallback(async (tripId: string) => {
    if (!uid) return;
    try {
      await deleteTripFromCloud(uid, tripId);
      if (tripId === currentTripId) setCurrentTripId(undefined);
    } catch { setError(i18n.t('copilot.error.delete_failed')); }
  }, [uid, currentTripId]);

  const shareCurrentTrip = useCallback(async (): Promise<string | null> => {
    if (!uid || specRef.current.status !== 'ready') return null;
    try {
      return await shareTripToCloud(uid, { tripSpec: specRef.current }, Date.now());
    } catch { setError(i18n.t('copilot.error.share_failed')); return null; }
  }, [uid]);

  // Rydd debounce-timer ved unmount.
  useEffect(() => () => { if (replanTimer.current) clearTimeout(replanTimer.current); }, []);

  return {
    phase, messages, tripSpec, suggestedChoices, thinking, planning, statusMsg, error, planResult,
    savedTrips, saving,
    learningEnabled: memory.enabled, learnedFacts: memory.facts,
    weather, weatherAlt, suggestBetterWeather,
    updateEndpoint, removeStop,
    sendMessage, confirmPlan, reset, retry,
    saveCurrentTrip, loadTrip, deleteSavedTrip, shareCurrentTrip, openSharedSpec,
    setLearningEnabled, clearLearnedMemory,
  };
}
